import {customAlphabet} from 'nanoid';

import {StatusCode} from 'common/util/shared-client';
import {ToastType} from 'common/components/toast/types';
import {addToast} from 'common/components/toast-area/toast-manager';
import {reporter} from 'common/report/error-reporting';
import {ReportName} from 'common/report/constants';
import {base58alphabet} from 'common/report/util/public/uuid-helpers';
import {FeatureRolloutName, FeatureRollouts} from 'common/report/util/feature-sampling';
import {CurrentProject} from 'common/report/queries/app-api/project-metadata-client';
import {ProjectDetails} from 'common/report/stores/project-details';
import {isCurrentProjectInternal} from 'common/report/util/index';
import {isTestEnv} from 'common/report/util/env';

export const CleanerURLsEnabledTrackingProp = `Cleaner URLs Enabled`;

export enum BookmarkURLTrackEvents {
  LoadLongURL = `[Cleaner URL] Load Long URL`,
}

export type LoadLongURLTrackingProps = {
  tab: ReportName;
  [CleanerURLsEnabledTrackingProp]: boolean;
  'Bookmark ID'?: number;
  'Is Fastedit Mode'?: boolean;
};

const SLUG_LENGTH = 12;

// We're now using base58 rather than base64. Keeping base64 regex for now to maintain support for existing base64
// slugs (only internal projects during dogfooding). Can change regex to base58 (here and in server validation) in
// the future once usage of base64 slugs approaches 0 (although probably not worth the effort)
const SLUG_REGEX = new RegExp(`^[0-9a-zA-Z_-]{${SLUG_LENGTH}}$`);

export function isValidBookmarkURLSlug(shortCode: string): boolean {
  return Boolean(SLUG_REGEX.exec(shortCode));
}

const base58NanoId = customAlphabet(base58alphabet);

export function generateBookmarkURLSlug() {
  return base58NanoId(SLUG_LENGTH);
}

export function handleFetchBookmarkURLError(err, params: {slug: string; projectId: number; reportName: ReportName}) {
  reporter.error(err, `Error fetching bookmark URL`, params);

  let message = `Error loading report`;

  if (err.status === StatusCode.NotFound) {
    message = `Report does not exist`;
  }

  addToast({message, type: ToastType.ERROR});
}

export function hasCleanerUrlsEnabled(project: CurrentProject | ProjectDetails | undefined): boolean | undefined {
  if (isTestEnv()) {
    return true;
  }

  if (!project) {
    return undefined;
  }

  const allowedOrgs = [
    1930177, // Getir (to test latency on a large EU customer)
  ];
  if (isCurrentProjectInternal() || allowedOrgs.includes(project.organizationId)) {
    return true;
  }

  return FeatureRollouts[FeatureRolloutName.CleanerUrls].isEnabled(project);
}
